import { useCallback, useEffect, useRef, useState, KeyboardEvent } from 'react';
import { api } from '../../lib/api';
import { useSelectionStore } from '../../state/selectionStore';

export function PromptPanel() {
  const prompt = useSelectionStore((state) => state.prompt ?? '');
  const select = useSelectionStore((state) => state.set);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [promptHistory, setPromptHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [temporaryPrompt, setTemporaryPrompt] = useState('');
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMatchIndex, setSearchMatchIndex] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const searchOriginalPromptRef = useRef('');
  const searchOriginalSelectionRef = useRef({ start: 0, end: 0 });

  // Load prompt history on mount
  useEffect(() => {
    const loadHistory = async () => {
      try {
        const prompts = await api().services.context.prompts(200);
        setPromptHistory(prompts || []);
      } catch (error) {
        console.error('[PromptPanel] Failed to load prompt history:', error);
      }
    };
    loadHistory();
  }, []);

  useEffect(() => {
    if (!isSearchMode) return;

    if (!searchQuery) {
      if (prompt !== searchOriginalPromptRef.current) {
        select({ prompt: searchOriginalPromptRef.current });
      }
      return;
    }

    const normalizedQuery = searchQuery.toLowerCase();
    const matches = promptHistory.filter((entry) => entry.toLowerCase().includes(normalizedQuery));

    if (matches.length > 0) {
      const normalizedIndex = ((searchMatchIndex % matches.length) + matches.length) % matches.length;
      const matchToShow = matches[normalizedIndex];
      if (prompt !== matchToShow) {
        select({ prompt: matchToShow });
      }
    } else {
      // No matches - show the search query itself
      if (prompt !== searchQuery) {
        select({ prompt: searchQuery });
      }
    }
  }, [isSearchMode, promptHistory, prompt, searchQuery, searchMatchIndex, select]);

  const handleSubmit = useCallback(async () => {
    const trimmedPrompt = prompt.trim();
    if (!trimmedPrompt || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await api().agent.prompt(trimmedPrompt);
      select({ prompt: '' });
      setHistoryIndex(-1);
      setTemporaryPrompt('');

      // Refresh prompt history
      const prompts = await api().services.context.prompts(200);
      setPromptHistory(prompts || []);
    } catch (error) {
      console.error('[PromptPanel] Failed to submit prompt:', error);
    } finally {
      setIsSubmitting(false);
      // Re-focus textarea after submit completes
      requestAnimationFrame(() => {
        textareaRef.current?.focus();
      });
    }
  }, [prompt, isSubmitting, select]);

  const exitSearchMode = useCallback(
    (options?: { restoreValue?: boolean }) => {
      if (!isSearchMode) return null;

      const { restoreValue = true } = options ?? {};
      const originalPrompt = searchOriginalPromptRef.current;
      const originalSelection = searchOriginalSelectionRef.current;

      setIsSearchMode(false);
      setSearchQuery('');
      setSearchMatchIndex(0);

      if (restoreValue) {
        select({ prompt: originalPrompt });

        const textarea = textareaRef.current;
        if (textarea) {
          requestAnimationFrame(() => {
            textarea.focus();
            textarea.setSelectionRange(originalSelection.start, originalSelection.end);
          });
        }
      }

      searchOriginalPromptRef.current = '';
      searchOriginalSelectionRef.current = { start: 0, end: 0 };

      return { originalPrompt, originalSelection };
    },
    [isSearchMode, select],
  );

  const navigateHistoryUp = useCallback(
    (currentPrompt: string, currentHistoryIndex: number) => {
      if (promptHistory.length === 0) return;

      if (currentHistoryIndex === -1) {
        setTemporaryPrompt(currentPrompt);
      }

      const newIndex =
        currentHistoryIndex === -1 ? 0 : Math.min(currentHistoryIndex + 1, promptHistory.length - 1);
      setHistoryIndex(newIndex);
      select({ prompt: promptHistory[newIndex] });
    },
    [promptHistory, select],
  );

  const navigateHistoryDown = useCallback(
    (currentHistoryIndex: number) => {
      if (currentHistoryIndex === -1) return;

      const newIndex = currentHistoryIndex - 1;
      if (newIndex < 0) {
        setHistoryIndex(-1);
        select({ prompt: temporaryPrompt });
      } else {
        setHistoryIndex(newIndex);
        select({ prompt: promptHistory[newIndex] });
      }
    },
    [promptHistory, temporaryPrompt, select],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      const isSearchShortcut =
        (event.ctrlKey || event.metaKey) &&
        !event.altKey &&
        !event.shiftKey &&
        event.key.toLowerCase() === 'r';

      if (isSearchShortcut) {
        event.preventDefault();

        if (!isSearchMode) {
          searchOriginalPromptRef.current = prompt;

          const textarea = textareaRef.current;
          if (textarea) {
            const selectionStart = textarea.selectionStart ?? prompt.length;
            const selectionEnd = textarea.selectionEnd ?? prompt.length;
            searchOriginalSelectionRef.current = { start: selectionStart, end: selectionEnd };
          } else {
            searchOriginalSelectionRef.current = { start: prompt.length, end: prompt.length };
          }

          setIsSearchMode(true);
          setSearchQuery('');
          setSearchMatchIndex(0);
          setHistoryIndex(-1);
        } else {
          // Cycle to next match
          setSearchMatchIndex((prev) => prev + 1);
        }

        return;
      }

      if (isSearchMode) {
        // Ctrl+C: Cancel search
        if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'c') {
          event.preventDefault();
          exitSearchMode({ restoreValue: true });
          setHistoryIndex(-1);
          return;
        }

        // Ctrl+A: Jump to start of line
        if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'a') {
          event.preventDefault();
          setSearchQuery('');
          setSearchMatchIndex(0);
          return;
        }

        // Ctrl+K: Clear to end of line
        if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
          event.preventDefault();
          setSearchQuery('');
          setSearchMatchIndex(0);
          return;
        }

        if (event.key === 'Escape') {
          event.preventDefault();
          exitSearchMode({ restoreValue: true });
          setHistoryIndex(-1);
          return;
        }

        if (event.key === 'Enter' && !event.shiftKey && !event.ctrlKey && !event.metaKey) {
          event.preventDefault();
          exitSearchMode({ restoreValue: false });
          setHistoryIndex(-1);
          handleSubmit();
          return;
        }

        if (event.key === 'Backspace') {
          event.preventDefault();
          setSearchQuery((prev) => (prev.length > 0 ? prev.slice(0, prev.length - 1) : prev));
          setSearchMatchIndex(0);
          return;
        }

        if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
          event.preventDefault();

          const normalizedQuery = searchQuery.toLowerCase();
          const matches = promptHistory.filter((entry) => entry.toLowerCase().includes(normalizedQuery));

          if (matches.length > 0) {
            const direction = event.key === 'ArrowDown' ? 1 : -1;
            setSearchMatchIndex((prev) => prev + direction);
          }

          return;
        }

        const movementKeys = ['ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown'];
        if (movementKeys.includes(event.key)) {
          exitSearchMode({ restoreValue: false });
          setHistoryIndex(-1);
          return;
        } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
          event.preventDefault();
          setSearchQuery((prev) => prev + event.key);
          setSearchMatchIndex(0);
          return;
        } else {
          exitSearchMode({ restoreValue: false });
          setHistoryIndex(-1);
        }
      }

      if (event.key === 'Enter' && !event.shiftKey && !event.ctrlKey && !event.metaKey) {
        event.preventDefault();
        handleSubmit();
      } else if (event.key === 'Escape') {
        if (prompt.trim()) {
          select({ prompt: '' });
          setHistoryIndex(-1);
          setTemporaryPrompt('');
        } else {
          event.currentTarget.blur();
          setHistoryIndex(-1);
        }
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();

        navigateHistoryUp(prompt, historyIndex);
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();

        navigateHistoryDown(historyIndex);
      }
    },
    [
      exitSearchMode,
      handleSubmit,
      historyIndex,
      isSearchMode,
      prompt,
      navigateHistoryDown,
      navigateHistoryUp,
      promptHistory,
      searchQuery,
      select,
    ],
  );

  const handleMouseDown = useCallback(() => {
    if (isSearchMode) {
      exitSearchMode({ restoreValue: false });
      setHistoryIndex(-1);
    }
  }, [exitSearchMode, isSearchMode]);

  const handleBlur = useCallback(() => {
    if (isSearchMode) {
      exitSearchMode({ restoreValue: false });
      setHistoryIndex(-1);
    }
  }, [exitSearchMode, isSearchMode]);

  // Helper function to render prompt with highlighted search query
  const renderPromptWithHighlight = () => {
    if (!isSearchMode || !searchQuery || !prompt) {
      return null;
    }

    const normalizedQuery = searchQuery.toLowerCase();
    const normalizedPrompt = prompt.toLowerCase();

    // Find all occurrences
    const parts: JSX.Element[] = [];
    let lastIndex = 0;
    let searchIndex = 0;

    while (searchIndex < normalizedPrompt.length) {
      const index = normalizedPrompt.indexOf(normalizedQuery, searchIndex);
      if (index === -1) break;

      // Add text before match
      if (index > lastIndex) {
        parts.push(<span key={`text-${parts.length}`}>{prompt.substring(lastIndex, index)}</span>);
      }

      // Add highlighted match
      const matchText = prompt.substring(index, index + searchQuery.length);
      parts.push(
        <span key={`match-${parts.length}`} className="prompt-highlight-match">
          {matchText}
        </span>
      );

      lastIndex = index + searchQuery.length;
      searchIndex = lastIndex;
    }

    // Add remaining text
    if (lastIndex < prompt.length) {
      parts.push(<span key={`text-${parts.length}`}>{prompt.substring(lastIndex)}</span>);
    }

    return (
      <div className="prompt-highlight-overlay">
        {parts}
      </div>
    );
  };

  // Get match count and current match index for display
  const getMatchInfo = () => {
    if (!isSearchMode || !searchQuery) return null;

    const normalizedQuery = searchQuery.toLowerCase();
    const matches = promptHistory.filter((entry) => entry.toLowerCase().includes(normalizedQuery));

    if (matches.length === 0) {
      return { total: 0, current: 0 };
    }

    const normalizedIndex = ((searchMatchIndex % matches.length) + matches.length) % matches.length;

    return {
      total: matches.length,
      current: normalizedIndex + 1,
    };
  };

  const matchInfo = getMatchInfo();

  return (
    <div className="panel-shell prompt-panel">
      <div className="prompt-wrapper">
        {isSearchMode && (
          <div className="prompt-search-indicator">
            <span className="prompt-search-label">
              Search: <span className="prompt-search-query">{searchQuery || '_'}</span>
            </span>
            {matchInfo && matchInfo.total > 0 && (
              <span className="prompt-search-matches">
                {matchInfo.current}/{matchInfo.total}
              </span>
            )}
            {matchInfo && matchInfo.total === 0 && (
              <span className="prompt-search-no-matches">No matches</span>
            )}
          </div>
        )}
        <div className="prompt-container">
          <div className="prompt-textarea-wrapper">
            <textarea
              ref={textareaRef}
              className="prompt-textarea"
              placeholder="Type your prompt here..."
              value={prompt}
              onChange={(e) => select({ prompt: e.target.value })}
              onKeyDown={handleKeyDown}
              onMouseDown={handleMouseDown}
              onBlur={handleBlur}
              disabled={isSubmitting}
            />
            {renderPromptWithHighlight()}
          </div>
          <div className="prompt-actions">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!prompt.trim() || isSubmitting}
              className="btn"
            >
              {isSubmitting ? 'Sending...' : 'Send'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
